import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from "@angular/core";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { MailTemplateComponent } from "src/app/components/shared/secure-mail/mail-template/mail-template.component";
import { SaveMailComponent } from "src/app/components/shared/secure-mail/save-mail/save-mail.component";
import { EmailService } from "src/app/services/email-service";
import { Subject, of } from "rxjs";
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  takeUntil,
} from "rxjs/operators";

export interface EmailCompose {
  to: string;
  cc: string;
  subject: string;
  body: string; // HTML content
}

interface FooterStyleModel {
  heading: "p" | "h1" | "h2" | "h3";
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strike: boolean;
  align: "left" | "center" | "right";
  textColor: string;
  bgColor: string;
}

interface CellContext {
  cell: HTMLTableCellElement;
  row: HTMLTableRowElement;
  table: HTMLTableElement;
}

@Component({
  selector: "app-email-extracts",
  templateUrl: "./email-extracts.component.html",
  styleUrls: ["./email-extracts.component.scss"],
})
export class EmailExtractsComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  emailForm!: FormGroup;

  @ViewChild("bodyEditor") bodyEditor!: ElementRef<HTMLDivElement>;

  // 🔹 remember last selected table cell
  private currentCellCtx: CellContext | null = null;

  sec: any = {
    _showFooterToolbar: true,
    footer: <FooterStyleModel>{
      heading: "p",
      bold: false,
      italic: false,
      underline: false,
      strike: false,
      align: "left",
      textColor: "#000000",
      bgColor: "#ffffff",
    },
  };

  @Output() sendEmail = new EventEmitter<EmailCompose>();
  @Output() saveTemplate = new EventEmitter<EmailCompose>();

  uploadedFiles: File[] = [];
  templateAttachments: any[] = [];
  combinedAttachments: any[] = [];
  attachmentPopup: boolean = false;
  showTableTools: boolean = false;

  // Email suggestions for "from" field
  fromEmailSearch$ = new Subject<string>();
  filteredFromEmails: string[] = [];
  showFromSuggestions: boolean = false;
  private destroy$ = new Subject<void>();

  // Dummy email data
  private dummyEmails: string[] = [
    "admin@dataunveil.com",
    "support@dataunveil.com",
    "info@dataunveil.com",
    "contact@dataunveil.com",
    "noreply@dataunveil.com",
    "hr@dataunveil.com",
  ];
  showFormatTools: boolean = false;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private emailService: EmailService
  ) {
    this.emailForm = this.fb.group({
      // from: [""],
      to: ["", [Validators.required]],
      cc: [""],
      subject: [""],
      body: [""], // holds full HTML
    });
  }

  ngOnInit(): void {
    this.loadLoginAndFetchEmails();
    this.setupFromEmailSearch();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.syncEditorFromForm();
  }

  // ------------------ EMAIL SUGGESTIONS ------------------

  private setupFromEmailSearch(): void {
    this.fromEmailSearch$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        switchMap((searchTerm: string) => {
          // Simulate API call with dummy data
          return of(this.filterEmails(searchTerm));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((filteredEmails: string[]) => {
        this.filteredFromEmails = filteredEmails;
        this.showFromSuggestions = filteredEmails.length > 0;
      });
  }

  private filterEmails(searchTerm: string): string[] {
    if (!searchTerm || searchTerm.trim() === "") {
      return [];
    }

    const lowerSearchTerm = searchTerm.toLowerCase();
    return this.dummyEmails.filter((email) =>
      email.toLowerCase().includes(lowerSearchTerm)
    );
  }

  onFromInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    this.fromEmailSearch$.next(value);
  }

  selectFromEmail(email: string): void {
    this.emailForm.patchValue({ from: email });
    this.showFromSuggestions = false;
    this.filteredFromEmails = [];
  }

  hideFromSuggestions(): void {
    // Delay hiding to allow click event on suggestion to fire
    setTimeout(() => {
      this.showFromSuggestions = false;
    }, 200);
  }

  // ------------------ EDITOR SYNC ------------------

  private syncEditorFromForm(): void {
    if (!this.bodyEditor) return;
    const html = this.emailForm.get("body")?.value || "";
    this.bodyEditor.nativeElement.innerHTML = html;
  }

  onBodyInput(): void {
    if (!this.bodyEditor) return;
    const html = this.bodyEditor.nativeElement.innerHTML;
    this.emailForm.patchValue({ body: html }, { emitEvent: false });
  }

  private focusEditor(): void {
    if (this.bodyEditor && this.bodyEditor.nativeElement) {
      this.bodyEditor.nativeElement.focus();
    }
  }

  private applyCommand(command: string, value: any = null): void {
    this.focusEditor();
    document.execCommand(command, false, value);
    this.onBodyInput();
  }

  private insertHtmlAtCursor(html: string): void {
    this.focusEditor();
    document.execCommand("insertHTML", false, html);
    this.onBodyInput();
  }

  // 🔹 update current cell when user changes selection in editor
  onEditorSelectionChange(): void {
    this.currentCellCtx = this.getSelectedCellContext();
  }

  toggleTableTools(): void {
    this.showTableTools = !this.showTableTools;
  }

  toggleFormatTools(): void {
    this.showFormatTools = !this.showFormatTools;
  }

  // ------------------ TABLE SUPPORT (ADVANCED) ------------------

  /** Insert a default styled table at cursor */
  insertDefaultTable(): void {
    const tableHtml = `
      <table border="1" cellpadding="8" cellspacing="0" style="border-collapse:collapse;width:100%;margin:12px 0;font-size:13px;">
        <thead>
          <tr style="background:#588abb;color:#ffffff;">
            <th
              style="
                text-align:left;
                border:1px solid #ddd;
                padding:8px;
                font-family:Arial, sans-serif;
                font-size:13px;
                font-weight:bold;
              "
            >
              Column 1
            </th>
            <th
              style="
                text-align:left;
                border:1px solid #ddd;
                padding:8px;
                font-family:Arial, sans-serif;
                font-size:13px;
                font-weight:bold;
              "
            >
              Column 2
            </th>
            <th
              style="
                text-align:left;
                border:1px solid #ddd;
                padding:8px;
                font-family:Arial, sans-serif;
                font-size:13px;
                font-weight:bold;
              "
            >
              Column 3
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td
              style="
                border:1px solid #ddd;
                padding:8px;
                background:#ffffff;
                font-family:Arial, sans-serif;
                font-size:13px;
              "
            >
              Row 1 - Cell 1
            </td>
            <td
              style="
                border:1px solid #ddd;
                padding:8px;
                background:#ffffff;
                font-family:Arial, sans-serif;
                font-size:13px;
              "
            >
              Row 1 - Cell 2
            </td>
            <td
              style="
                border:1px solid #ddd;
                padding:8px;
                background:#ffffff;
                font-family:Arial, sans-serif;
                font-size:13px;
              "
            >
              Row 1 - Cell 3
            </td>
          </tr>
          <tr>
            <td
              style="
                border:1px solid #ddd;
                padding:8px;
                background:#ffffff;
                font-family:Arial, sans-serif;
                font-size:13px;
              "
            >
              Row 2 - Cell 1
            </td>
            <td
              style="
                border:1px solid #ddd;
                padding:8px;
                background:#ffffff;
                font-family:Arial, sans-serif;
                font-size:13px;
              "
            >
              Row 2 - Cell 2
            </td>
            <td
              style="
                border:1px solid #ddd;
                padding:8px;
                background:#ffffff;
                font-family:Arial, sans-serif;
                font-size:13px;
              "
            >
              Row 2 - Cell 3
            </td>
          </tr>
        </tbody>
      </table>
    `;
    this.insertHtmlAtCursor(tableHtml);
  }

  /** locate currently selected cell + row + table based on selection */
  private getSelectedCellContext(): CellContext | null {
    if (!this.bodyEditor) return null;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    let node = selection.anchorNode as Node | null;
    if (!node) return null;

    if (node.nodeType === Node.TEXT_NODE) {
      node = node.parentElement;
    }

    while (node && node !== this.bodyEditor.nativeElement) {
      const el = node as HTMLElement;
      const tag = el.tagName;
      if (tag === "TD" || tag === "TH") {
        const cell = el as HTMLTableCellElement;
        const row = cell.parentElement as HTMLTableRowElement;
        const table = row.closest("table") as HTMLTableElement | null;
        if (table) {
          return { cell, row, table };
        }
        return null;
      }
      node = (node as HTMLElement).parentElement;
    }

    return null;
  }

  insertRowBelow(): void {
    const ctx = this.currentCellCtx || this.getSelectedCellContext();
    if (!ctx) return;

    const { row } = ctx;
    const newRow = row.cloneNode(true) as HTMLTableRowElement;

    Array.from(newRow.cells).forEach((cell) => {
      cell.innerHTML = "&nbsp;";
    });

    row.parentElement?.insertBefore(newRow, row.nextSibling);
    this.onBodyInput();
  }

  insertRowAbove(): void {
    const ctx = this.currentCellCtx || this.getSelectedCellContext();
    if (!ctx) return;

    const { row } = ctx;
    const newRow = row.cloneNode(true) as HTMLTableRowElement;

    Array.from(newRow.cells).forEach((cell) => {
      cell.innerHTML = "&nbsp;";
    });

    row.parentElement?.insertBefore(newRow, row);
    this.onBodyInput();
  }

  deleteRow(): void {
    const ctx = this.currentCellCtx || this.getSelectedCellContext();
    if (!ctx) return;

    const { row, table } = ctx;
    const rowIndex = row.rowIndex;
    table.deleteRow(rowIndex);
    this.onBodyInput();
  }

  insertColumnRight(): void {
    const ctx = this.currentCellCtx || this.getSelectedCellContext();
    if (!ctx) return;

    const { cell, table } = ctx;
    const index = cell.cellIndex;

    Array.from(table.rows).forEach((row) => {
      const refCell = row.cells[index] || row.cells[row.cells.length - 1];
      const newCell = refCell.cloneNode(true) as HTMLTableCellElement;
      newCell.innerHTML = "&nbsp;";
      row.insertBefore(newCell, refCell.nextSibling);
    });

    this.onBodyInput();
  }

  insertColumnLeft(): void {
    const ctx = this.currentCellCtx || this.getSelectedCellContext();
    if (!ctx) return;

    const { cell, table } = ctx;
    const index = cell.cellIndex;

    Array.from(table.rows).forEach((row) => {
      const refCell = row.cells[index];
      const newCell = refCell.cloneNode(true) as HTMLTableCellElement;
      newCell.innerHTML = "&nbsp;";
      row.insertBefore(newCell, refCell);
    });

    this.onBodyInput();
  }

  deleteColumn(): void {
    const ctx = this.currentCellCtx || this.getSelectedCellContext();
    if (!ctx) return;

    const { cell, table } = ctx;
    const index = cell.cellIndex;

    Array.from(table.rows).forEach((row) => {
      if (row.cells.length > index) {
        row.deleteCell(index);
      }
    });

    this.onBodyInput();
  }

  deleteTable(): void {
    const ctx = this.currentCellCtx || this.getSelectedCellContext();
    if (!ctx) return;

    const { table } = ctx;
    table.remove();
    this.onBodyInput();
  }

  // ---- TD / TH background color controls ----

  setThBackground(event: Event): void {
    const color = (event.target as HTMLInputElement).value;
    const ctx = this.currentCellCtx || this.getSelectedCellContext();
    if (!ctx) return;

    const { cell } = ctx;
    if (cell.tagName === "TH") {
      (cell as HTMLElement).style.background = color;
      this.onBodyInput();
    }
  }

  setTdBackground(event: Event): void {
    const color = (event.target as HTMLInputElement).value;
    const ctx = this.currentCellCtx || this.getSelectedCellContext();
    if (!ctx) return;

    const { cell } = ctx;
    if (cell.tagName === "TD") {
      (cell as HTMLElement).style.background = color;
      this.onBodyInput();
    }
  }

  // ------------------ EMAIL ACTIONS ------------------

  onSend(): void {
    // if (this.emailForm.valid) {
    //   this.sendEmail.emit(this.emailForm.value as EmailCompose);
    // }

    if (!this.emailForm.valid) {
      this.snackBar.open(
        "Please fill in all fields before sending the email.",
        "Close",
        {
          duration: 3000,
          panelClass: ["error-snackbar"],
        }
      );
      return;
    }

    // Prepare recipients
    const toRecipients = this.emailForm
      .get("to")
      ?.value.split(",")
      .map((email: string) => email.trim())
      .filter((email: string) => email.length > 0);
    const toString = toRecipients.join(",");

    // Prepare CC recipients
    const ccValue = this.emailForm.get("cc")?.value || "";
    const ccRecipients = ccValue
      ? ccValue
          .split(",")
          .map((email: string) => email.trim())
          .filter((email: string) => email.length > 0)
      : [];
    const ccString = ccRecipients.join(",");

    // console.log("Email sent:", {
    //   to: toString,
    //   cc: ccString,
    //   subject: this.subject,
    //   message: this.message .replace(/<br\s*\/?>/gi, "\n")
    //   .replace(/&nbsp;/g, " ");,
    //   attachments: this.uploadedFiles.map((file) => file.name),
    // });

    this.emailService
      .sendEmail(
        toString,
        this.emailForm.get("subject")?.value,
        this.emailForm.get("body")?.value,
        this.uploadedFiles,
        ccString
      )
      .then(() => {
        this.snackBar.open("Email sent successfully!", "Close", {
          duration: 3000,
          panelClass: ["success-snackbar"],
        });

        // ✅ Reset form fields
        this.uploadedFiles = [];
        this.combinedAttachments = [];
        this.emailForm.reset();
        this.bodyEditor.nativeElement.innerHTML = "";

        // this.popUpClose();
      })
      .catch((error) => {
        console.error("Error sending email:", error);
        this.snackBar.open("Failed to send email. Please try again.", "Close", {
          duration: 3000,
          panelClass: ["error-snackbar"],
        });
      });
  }

  onSaveTemplate(): void {
    // if (this.emailForm.valid) {
    //   this.saveTemplate.emit(this.emailForm.value as EmailCompose);
    // }
    const templateData = {
      subject: this.emailForm.get("subject")?.value || "",
      body: this.emailForm.get("body")?.value || "",
      attachments: this.uploadedFiles,
    };

    this.dialog.open(SaveMailComponent, {
      height: "40%",
      disableClose: false,
      panelClass: "du-custom-modalbox",
      data: templateData,
    });
  }

  // ------------------ FOOTER TOOLBAR HELPERS ------------------

  private ensureFooter(sec: any): FooterStyleModel {
    if (!sec.footer) {
      sec.footer = <FooterStyleModel>{
        heading: "p",
        bold: false,
        italic: false,
        underline: false,
        strike: false,
        align: "left",
        textColor: "#000000",
        bgColor: "#ffffff",
      };
    }
    return sec.footer as FooterStyleModel;
  }

  // Heading
  getFooterHeading(sec: any): string {
    const footer = this.ensureFooter(sec);
    return footer.heading;
  }

  onFooterHeadingChange(sec: any, event: Event | string): void {
    let value: string;

    if (typeof event === "string") {
      value = event;
    } else {
      const target = event.target as HTMLSelectElement;
      value = target.value;
    }

    const safeValue = (value || "p") as "p" | "h1" | "h2" | "h3";
    const footer = this.ensureFooter(sec);
    footer.heading = safeValue;

    this.applyCommand("formatBlock", safeValue.toUpperCase());
  }

  // Bold
  isFooterBold(sec: any): boolean {
    const footer = this.ensureFooter(sec);
    return !!footer.bold;
  }

  toggleFooterBold(sec: any): void {
    const footer = this.ensureFooter(sec);
    footer.bold = !footer.bold;
    this.applyCommand("bold");
  }

  // Italic
  isFooterItalic(sec: any): boolean {
    const footer = this.ensureFooter(sec);
    return !!footer.italic;
  }

  toggleFooterItalic(sec: any): void {
    const footer = this.ensureFooter(sec);
    footer.italic = !footer.italic;
    this.applyCommand("italic");
  }

  // Underline
  isFooterUnderline(sec: any): boolean {
    const footer = this.ensureFooter(sec);
    return !!footer.underline;
  }

  toggleFooterUnderline(sec: any): void {
    const footer = this.ensureFooter(sec);
    footer.underline = !footer.underline;
    this.applyCommand("underline");
  }

  // Strike
  isFooterStrike(sec: any): boolean {
    const footer = this.ensureFooter(sec);
    return !!footer.strike;
  }

  toggleFooterStrike(sec: any): void {
    const footer = this.ensureFooter(sec);
    footer.strike = !footer.strike;
    this.applyCommand("strikeThrough");
  }

  // Alignment
  isFooterAlign(sec: any, align: "left" | "center" | "right"): boolean {
    const footer = this.ensureFooter(sec);
    return footer.align === align;
  }

  setFooterAlign(sec: any, align: "left" | "center" | "right"): void {
    const footer = this.ensureFooter(sec);
    footer.align = align;

    const map: Record<"left" | "center" | "right", string> = {
      left: "justifyLeft",
      center: "justifyCenter",
      right: "justifyRight",
    };

    this.applyCommand(map[align]);
  }

  // Text Color
  getFooterTextColor(sec: any): string {
    const footer = this.ensureFooter(sec);
    return footer.textColor || "#000000";
  }

  onFooterTextColorChange(sec: any, event: Event): void {
    const footer = this.ensureFooter(sec);
    const value = (event.target as HTMLInputElement).value;
    footer.textColor = value;
    this.applyCommand("foreColor", value);
  }

  // Background Color
  getFooterBgColor(sec: any): string {
    const footer = this.ensureFooter(sec);
    return footer.bgColor || "#ffffff";
  }

  onFooterBgColorChange(sec: any, event: Event): void {
    const footer = this.ensureFooter(sec);
    const value = (event.target as HTMLInputElement).value;
    footer.bgColor = value;
    this.applyCommand("hiliteColor", value);
  }

  getFooterStyles(sec: any): { [key: string]: string } {
    const footer = this.ensureFooter(sec);
    const styles: { [key: string]: string } = {
      "text-align": footer.align,
      color: footer.textColor,
      "background-color": footer.bgColor,
    };

    styles["font-weight"] = footer.bold ? "600" : "400";
    styles["font-style"] = footer.italic ? "italic" : "normal";
    styles["text-decoration"] = [
      footer.underline ? "underline" : "",
      footer.strike ? "line-through" : "",
    ]
      .filter(Boolean)
      .join(" ");

    return styles;
  }

  // ------------------ TEMPLATE DIALOG ------------------

  openMailTemplates(): void {
    const dialogRef = this.dialog.open(MailTemplateComponent, {
      height: "auto",
      // height: "70%",
      maxHeight: "70%",
      width: "30%",
      disableClose: false,
      panelClass: "du-custom-modalbox",
      data: "USER_ACCESS_REQUEST",
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      this.uploadedFiles = [];
      this.templateAttachments = [];

      if (!result) return;
      if (result.attachments) {
        this.templateAttachments = result.attachments?.Attachments || [];
        this.emailForm.patchValue({
          subject:
            result.attachments?.email_subject ??
            this.emailForm.get("subject")?.value,
          body: result.attachments?.email_body,
        });
        this.syncEditorFromForm();
        this.updateCombinedAttachments();
      } else {
        this.emailForm.patchValue({
          subject: result.email_subject ?? this.emailForm.get("subject")?.value,
          body: result.email_body ?? this.emailForm.get("body")?.value,
        });
        this.syncEditorFromForm();
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.attachmentPopup = true;
      for (let i = 0; i < input.files.length; i++) {
        this.uploadedFiles.push(input.files[i]);
      }
      this.updateCombinedAttachments();
    }
  }

  removeUploadedFile(index: number): void {
    this.uploadedFiles.splice(index, 1);
    this.combinedAttachments.splice(index, 1);
    this.templateAttachments.splice(index, 1);
    this.updateCombinedAttachments();
  }

  updateCombinedAttachments(): void {
    this.combinedAttachments = [];

    this.templateAttachments.forEach((file: any) => {
      this.combinedAttachments.push({
        name: file.attachment_file_name,
        url: file.attachment_file_name,
      });
    });

    this.uploadedFiles.forEach((file: File, index: number) => {
      this.combinedAttachments.push({
        name: file.name,
        file: file,
        isTemplateFile: false,
        index: index,
      });
    });
  }

  loadLoginAndFetchEmails(): void {
    this.emailService.login().subscribe({
      next: async (result) => {
        if (result === "success") {
          // this.isLoggedIn = true;
          console.log("User successfully logged in");
        } else {
          // this.isLoggedIn = false;
          console.log("Login failed");
        }
      },
      error: (err) => {
        // this.isLoggedIn = false;
        console.error("Login error:", err);
      },
    });
  }
}
